import { useState, useCallback, useEffect } from 'react';
import { useNotification } from '@/stores/notificationStore';
import { HttpAdminAdapter } from '@/services/admin/HttpAdminAdapter';
import type {
  UsageMetrics,
  PerformanceMetrics,
} from '@/services/admin/AdminService';

/**
 * Hook for fetching monitoring metrics
 */
export const useMonitoring = () => {
  const [adminService] = useState(() => new HttpAdminAdapter());
  const [usageMetrics, setUsageMetrics] = useState<UsageMetrics | null>(null);
  const [performanceMetrics, setPerformanceMetrics] =
    useState<PerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showNotification } = useNotification();

  const fetchMetrics = useCallback(
    async (refresh: boolean = false) => {
      setLoading(true);
      setError(null);
      try {
        const [usage, performance] = await Promise.all([
          adminService.getUsageMetrics(refresh),
          adminService.getPerformanceMetrics(),
        ]);
        setUsageMetrics(usage);
        setPerformanceMetrics(performance);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch metrics';
        setError(errorMessage);
        showNotification(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    },
    [adminService, showNotification]
  );

  const refreshMetrics = useCallback(() => {
    return fetchMetrics(true);
  }, [fetchMetrics]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    usageMetrics,
    performanceMetrics,
    loading,
    error,
    fetchMetrics,
    refreshMetrics,
  };
};

export default useMonitoring;
